import {
  ConditionDTO,
  ExperimentClient,
  ExperimentDTO,
  ExperimentInfoDTO,
  ExperimentInfoImagingScheduleDTO,
  FileResultDto,
  IncubationTemperatureDTO,
  InspectionClient,
  LayerDTO,
  NavigatorMapDTO,
  PlateClient,
  ScreenLotDTO,
  UpdatedExperimentInfo,
  SetupTemperatureDTO,
  XMLScreenDataDTO,
  XmlScreenImportResultDto,
  SaveExperimentDTO,
  AlertDTO,
  TemperatureType,
  PlateAtImagerResponseDTO,
  CapturePropertyDTO,
  ScoresOfWellDropsDTO,
  AllPlatesScoreDTO,
  ScreenRepairInfoDTO,
  ScreenRepairUpdateExpInfo,
  ExperimentTuningValuesDTO,
  ResultWithValueOfString,
  FrapExporterClient,
  ImagingClient,
  ExperimentReportCompositeImageDTO,
  CompositeImageWithDrawingDTO,
  PlateIngredientAndDrawingRequestDTO,
  SaveExperimentResponseDTO,
  BulkSaveImagingScheduleDTO,
} from 'business/models/Models';
import ModelFactory from 'business/models/ModelFactory';
import { GetAllPlatesIngredients } from 'business/services/IngredientService';
import BaseService from 'business/services/BaseService';
import { validateWellsAndDrops } from 'business/plateviewer/Generate/GenerateCommon';
import { WellDetailsStates } from 'business/models/CustomModels';
import { Plate } from 'components/Experiment/constants';
import {
  getPlainDrawingIdentifiers,
  getPlainExperiment,
  getPlainIngredient,
} from 'transformers/experiment';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';

export default class ExperimentService extends BaseService {
  [x: string]: any;
  async getExperimentService(
    expId,
    initiatePreemptiveCache: boolean = false,
    signal?: AbortSignal,
  ) {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true, signal);

    const root = getPlainExperiment(
      await exp.getExperiment(expId, initiatePreemptiveCache),
    );

    return root;
  }
  async getExperimentInfoService(expId): Promise<ExperimentInfoDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);

    const root = await exp.getExperimentInfo(expId);

    return root;
  }
  async getScreen(screenId, userId): Promise<ExperimentDTO> {
    const plate: PlateClient = ModelFactory(PlateClient);
    const root = await plate.getScreen(screenId, userId);
    return root;
  }

  async loadAllPlatesIngredients(
    plates: Plate[],
    containerMaxDropVolume: number,
    layers: LayerDTO[],
    regionName: string,
    cpID: number,
  ) {
    const plateIDs = plates.map((plate) => plate.plateId);
    const reqData: PlateIngredientAndDrawingRequestDTO =
      new PlateIngredientAndDrawingRequestDTO();
    reqData.plateIDs = plateIDs;
    reqData.regionName = regionName;
    reqData.cpID = cpID;
    const result = await GetAllPlatesIngredients(reqData);
    plates.forEach((plate) => {
      const filteredResult = result.filter((x) => x.plateId == plate.plateId)[0];
      if (filteredResult) {
        plate.ingredients = getPlainIngredient(filteredResult.ingredients);
        plate.drawingIdentifiers = getPlainDrawingIdentifiers(
          filteredResult.drawingsIdentifier,
        );
      }
    });
    validateWellsAndDrops(plates, containerMaxDropVolume, layers);
  }

  async getLayers(expId: number) {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getLayers(expId);
    return result;
  }

  async getExpListLayers(expIds: number[]) {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getExperimentLayers(expIds);
    return result;
  }

  async getLayerDetails(expId: number) {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getLayerDetails(expId);
    return result;
  }

  async getScreenLotDetails(screenLotId: number): Promise<ScreenLotDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getScreenLayerDetails(screenLotId);
    return result;
  }

  async getExperimentContainerId(expId: number): Promise<number> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getExperimentContainerId(expId);
    return result;
  }

  async getCompositeNavigatorDataForced(expId: number): Promise<NavigatorMapDTO> {
    const inspection: InspectionClient = ModelFactory(InspectionClient, false, true);
    const result = await inspection.getCompositeNavigator(expId);
    return result;
  }
  async getCompositeNavigatorData(expId: number): Promise<NavigatorMapDTO> {
    const inspection: InspectionClient = ModelFactory(InspectionClient);
    const result = await inspection.getCompositeNavigator(expId);
    return result;
  }

  async getExperimentInfo(expId: number): Promise<ExperimentInfoDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getExperimentInfo(expId);
    return result;
  }

  async getImagingSchedule(
    expId: number,
    forceFetch = false,
  ): Promise<ExperimentInfoImagingScheduleDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, forceFetch);
    const result = await exp.getImagingSchedule(expId);
    return result;
  }

  async getXMlScreenRawData(expId: number): Promise<XMLScreenDataDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getXMLScreenRawData(expId);
    return result;
  }

  async getXMlScreenFileData(expId: number): Promise<FileResultDto> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getXMLScreenFileData(expId);
    return result;
  }

  async parseXMlScreenFile(
    fileData: string,
    shouldUseUnsedStock: boolean | null,
  ): Promise<XmlScreenImportResultDto> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    try {
      const result = await exp.parseXmlFile(fileData, shouldUseUnsedStock);
      return result;
    } catch {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Failed to parse XML file, the provided XML file is invalid or has a wrong format',
      );
      return null;
    }
  }

  async saveExperimentInfo(expInfo: ExperimentInfoDTO): Promise<UpdatedExperimentInfo> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.saveExperimentInfo(expInfo);
    return result;
  }

  async getWellsDataAsTextFile(wellIds: number[], expId = -1): Promise<FileResultDto> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getSelectedWellsTextFileData(wellIds, expId);
    return result;
  }

  async parseTextScreenFile(fileData: string): Promise<ConditionDTO[]> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.parseTextScreenFile(fileData);
    return result;
  }

  async isTemperatureInUse(ids: number[], type: TemperatureType): Promise<boolean> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result: boolean = await exp.isTemperatureInUse(ids, type);
    return result;
  }
  async getSetupTemperatures(): Promise<SetupTemperatureDTO[]> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result: SetupTemperatureDTO[] = await exp.getSetupTemperatures();
    return result;
  }

  async getIncubationTemperatures(): Promise<IncubationTemperatureDTO[]> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result: IncubationTemperatureDTO[] = await exp.getIncubationTemperatures();
    return result;
  }

  async saveExperiment(exp: SaveExperimentDTO) {
    const expService: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    try {
      const result: SaveExperimentResponseDTO = await expService.saveExperiment(exp);
      return result;
    } catch (error) {
      const statusCode = error.statusCode || error.StatusCode;
      if (statusCode === 451 || statusCode >= 500) {
        if (error instanceof Error) {
          throw error;
        } else {
          throw new Error(
            error.ErrorMessage ?? 'Failed to save experiment with status: ' + statusCode,
          );
        }
      }
      const message =
        error.errorMessage || 'Something went wrong, cannot save experiment!';
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `${message} Please refresh the page and try again.`,
      );
      return null;
    }
  }
  async getExperimentInfoByTreeNodeId(treeNodeId: number): Promise<ExperimentInfoDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getExperimentInfoByTreeNodeId(treeNodeId);
    return result;
  }
  async getExperimentImageSettings(
    wellDetails: WellDetailsStates,
  ): Promise<CapturePropertyDTO[]> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const { plateID, dropNumber, wellNum, cpID, regionName, inspectionSet } = wellDetails;
    const result = await exp.getImagingSettings(
      plateID,
      dropNumber,
      wellNum,
      regionName,
      inspectionSet?.groupedInspections?.find((i) => i.plateId === plateID)?.imageBatchId,
      cpID,
    );
    return result;
  }
  async markExperimentAsDispensed(experimentID: number): Promise<AlertDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await exp.markExperimentAsDispensed(experimentID);
  }

  async markExperimentPlatesAsDispensed(plateIdsList: number[]): Promise<AlertDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await exp.markExperimentPlatesAsDispensed(plateIdsList);
  }

  public async getAllPlatesAtImagerStatus(
    experimentId: number,
  ): Promise<PlateAtImagerResponseDTO[]> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await exp.getAllPlatesAtImagerStatus(experimentId);
  }

  public async getExperimentsTotalImagesCount(experimentIds: number[]): Promise<number> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await exp.getExperimentsTotalImagesCount(experimentIds);
  }

  public async getExperimentPlateHitScores(
    experimentId: number,
  ): Promise<ScoresOfWellDropsDTO[]> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await exp.getExperimentPlateHitScores(experimentId);
  }
  public async getAllScoresOfExperimentPlateWiseByExpId(
    expId: number,
  ): Promise<AllPlatesScoreDTO> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await exp.getAllScoresOfExperimentPlateWiseByExpId(expId);
  }

  async getXMlExperimentFileData(expId: number): Promise<FileResultDto> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.getXMLExperimentFileData(expId);
    return result;
  }

  async repairScreenExperiments(
    screenRepairInfoDTO: ScreenRepairInfoDTO,
  ): Promise<ScreenRepairUpdateExpInfo> {
    const exp: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    const result = await exp.repairScreenExperiments(screenRepairInfoDTO);
    return result;
  }
  async setExperimentImaging(expId: number, imagingEnabled: boolean): Promise<boolean> {
    const expClient: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await expClient.setExperimentImaging(expId, imagingEnabled);
  }
  async getExperimentImageTuningValues(
    expId: number,
  ): Promise<ExperimentTuningValuesDTO[]> {
    const expClient: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await expClient.getExperimentTuningValues(expId);
  }
  async markExperimentAsNotSeen(expIds: number[]): Promise<boolean> {
    const expClient: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await expClient.markExperimentAsNotSeen(expIds);
  }
  async exportFrapData(
    plateId: number,
    imageBatchId: number,
    cpId: number,
    dropNumber: number,
  ): Promise<ResultWithValueOfString> {
    const frapExporterClient: FrapExporterClient = ModelFactory(
      FrapExporterClient,
      false,
      true,
    );
    return await frapExporterClient.getFrapDataPoints(
      plateId,
      imageBatchId,
      cpId,
      dropNumber,
    );
  }

  async getExperimentCompositeImageWithDrawings(
    compositeImageDTO: ExperimentReportCompositeImageDTO,
  ): Promise<CompositeImageWithDrawingDTO[]> {
    const imagingClient: ImagingClient = ModelFactory(ImagingClient, false, true);
    return await imagingClient.createCompositeImageWithDrawings(compositeImageDTO);
  }

  async getPlateDefaultImagingScheduleId(containerId: number): Promise<number> {
    const expClient: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await expClient.getPlateDefaultImagingScheduleId(containerId);
  }

  async saveBulkImagingSchedule(
    bulkImagingSchedules: BulkSaveImagingScheduleDTO[],
  ): Promise<number[]> {
    const expClient: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await expClient.saveBulkImagingSchedule(bulkImagingSchedules);
  }

  async getMaxDropNumberByNodeIds(nodeIds: number[]): Promise<number> {
    const expClient: ExperimentClient = ModelFactory(ExperimentClient, false, true);
    return await expClient.getMaxDropNumberByNodeIds(nodeIds);
  }
}
